/**
 * FAIR Dimension Scorer
 *
 * Maps structural analysis metrics to estimated F/A/I/R scores (0-100).
 * Labeled in UI as "Estimated from structural analysis" (not the full SHACL FAIR scoring).
 */

import type { FairDimensionScores, StructuralAnalysisData } from './types';

/**
 * Compute estimated FAIR dimension scores from structural analysis.
 *
 * F (Findability): annotation coverage + external namespaces
 * A (Accessibility): absence of malformed IRIs + well-formed namespaces
 * I (Interoperability): domain/range coverage + SHACL consistency + external vocab reuse
 * R (Reusability): hierarchy depth + disjointness + controlled vocab + annotations + orphans
 */
export function computeFairScores(
  structural: StructuralAnalysisData | null
): FairDimensionScores {
  if (!structural) {
    return { findability: 0, accessibility: 0, interoperability: 0, reusability: 0 };
  }

  return {
    findability: computeFindability(structural),
    accessibility: computeAccessibility(structural),
    interoperability: computeInteroperability(structural),
    reusability: computeReusability(structural),
  };
}

function computeFindability(s: StructuralAnalysisData): number {
  let score = 0;

  // rdfs:label coverage (40% weight)
  const labelPct = s.annotationCoverage?.rdfsLabel?.percentage ?? 0;
  score += (labelPct / 100) * 40;

  // rdfs:comment coverage (30% weight)
  const commentPct = s.annotationCoverage?.rdfsComment?.percentage ?? 0;
  score += (commentPct / 100) * 30;

  // External namespaces present (15% weight)
  const extNs = s.externalNamespaces?.length ?? 0;
  score += Math.min(extNs / 3, 1) * 15; // cap at 3 external namespaces for full score

  // Namespace richness / variety (15% weight)
  const breadthKeys = Object.keys(s.hierarchyBreadth || {}).length;
  score += Math.min(breadthKeys / 5, 1) * 15;

  return Math.round(Math.min(score, 100));
}

function computeAccessibility(s: StructuralAnalysisData): number {
  let score = 0;

  // Absence of malformed IRIs (50% weight)
  const malformedCount = s.shaclConsistency?.malformedIRIs?.length ?? 0;
  const malformedPenalty = Math.min(malformedCount * 10, 50);
  score += 50 - malformedPenalty;

  // Well-formed namespaces (25% weight) - proxy via external namespace count
  const extNs = s.externalNamespaces?.length ?? 0;
  score += extNs > 0 ? 25 : 10;

  // Prefix definitions / blank node absence (25% weight)
  const blankNodes = s.shaclConsistency?.blankNodeCount ?? 0;
  const blankPenalty = Math.min(blankNodes * 5, 25);
  score += 25 - blankPenalty;

  return Math.round(Math.min(Math.max(score, 0), 100));
}

function computeInteroperability(s: StructuralAnalysisData): number {
  let score = 0;

  // Domain coverage (25% weight)
  score += (s.propertiesWithDomain.percentage / 100) * 25;

  // Range coverage (25% weight)
  score += (s.propertiesWithRange.percentage / 100) * 25;

  // SHACL consistency (25% weight)
  const shaclIssues = (s.shaclConsistency?.undeclaredProperties?.length ?? 0)
    + (s.shaclConsistency?.phantomClasses?.length ?? 0);
  const shaclPenalty = Math.min(shaclIssues * 5, 25);
  score += 25 - shaclPenalty;

  // External vocab reuse (25% weight)
  const extNs = s.externalNamespaces?.length ?? 0;
  score += Math.min(extNs / 2, 1) * 25;

  return Math.round(Math.min(Math.max(score, 0), 100));
}

function computeReusability(s: StructuralAnalysisData): number {
  let score = 0;

  // Hierarchy depth (20% weight) - ideal is 3-7
  const depth = s.hierarchyDepth.value;
  if (depth >= 3 && depth <= 7) {
    score += 20;
  } else if (depth >= 2) {
    score += 12;
  } else if (depth >= 1) {
    score += 6;
  }

  // Disjointness axioms (20% weight)
  const disjoint = s.disjointnessAnalysis;
  if (disjoint) {
    const totalGroups = disjoint.siblingGroups.length;
    const withDisjoint = disjoint.siblingGroups.filter(g => g.hasDisjointness).length;
    const disjointRatio = totalGroups > 0 ? withDisjoint / totalGroups : 0;
    score += disjointRatio * 20;
  }

  // Controlled vocabulary usage (20% weight)
  const cvIssues = s.controlledVocabularyIssues;
  if (cvIssues) {
    const cvPenalty = Math.min(cvIssues.candidateCount * 4, 20);
    score += 20 - cvPenalty;
  } else {
    score += 15; // No CV analysis means no known issues
  }

  // Annotation richness (20% weight) - combined label + comment
  const labelPct = s.annotationCoverage?.rdfsLabel?.percentage ?? 0;
  const commentPct = s.annotationCoverage?.rdfsComment?.percentage ?? 0;
  score += ((labelPct + commentPct) / 200) * 20;

  // Orphan class penalty (20% weight)
  const orphanCount = s.orphanClasses?.length ?? 0;
  const orphanPenalty = Math.min(orphanCount * 4, 20);
  score += 20 - orphanPenalty;

  return Math.round(Math.min(Math.max(score, 0), 100));
}
